import React, { useState } from 'react';
import { Notice } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Checkbox } from '@heartinternet/hi-ui';
import { Input } from '@heartinternet/hi-ui';
import { Link } from '@heartinternet/hi-ui';
import { Select } from '@heartinternet/hi-ui';
import { Text } from '@heartinternet/hi-ui';
import { Card } from '@heartinternet/hi-ui';
import { Form } from '@heartinternet/hi-ui';
import { FormField } from '@heartinternet/hi-ui';
import './AddHostingConfirmationPage.css';

export interface AddHostingContact {
  id: string;
  name: string;
  email: string;
}

export interface AddHostingConfirmationPageProps {
  /** "add" (confirmation) or "cleanup" (after welcome email / renewal setup) */
  action?: 'add' | 'cleanup';
  /** The domain that was activated. If falsy, renders the "no domain added" fallback. */
  addedDomain?: string | null;
  /** Package ID (OID, e.g. "p12345") for the newly created package */
  addedId?: string;
  /** Package type ID (hex) used for the package */
  addType?: string;
  /** Recommended nameserver hostnames for the customer */
  bestNameservers?: string[];
  /** Domains needing nameserver updates */
  nsChangeNeeded?: string[];
  /** Error codes from the CGI (`EBADEMAIL`, `EPACKAGECLEANUP`, etc.) */
  errors?: string[];
  /** Whether the reseller has a welcome email template configured for this package type */
  welcomeEmailEnabled?: boolean;
  /** True after /cleanup successfully sent the welcome email */
  sent?: boolean;
  /** Address book contacts sorted by name */
  contacts?: AddHostingContact[];
  /** Reseller has the renewal notifications feature */
  useNotifies?: boolean;
  /** Reseller has the address book feature */
  useAddressbook?: boolean;
  /** Current day of month (1–31) used as the renewal setup date */
  thisDay?: number;
  /** Current month (1–12) */
  thisMonth?: number;
}

const CLEANUP_ACTION = '/manage/manage-packages-w.cgi/cleanup';
const MANAGE_PACKAGES_URL = '/manage/manage-packages.cgi';

function errorMessageFor(code: string): string {
  if (code === 'EBADEMAIL') {
    return 'That email address looks invalid. Please double-check it and try again.';
  }
  return 'An internal error occurred. Please try again later.';
}

export const AddHostingConfirmationPage: React.FC<AddHostingConfirmationPageProps> = ({
  addedDomain = null,
  addedId = '',
  addType = '',
  bestNameservers = [],
  nsChangeNeeded = [],
  errors = [],
  welcomeEmailEnabled = false,
  sent = false,
  contacts = [],
  useNotifies = false,
  useAddressbook = false,
  thisDay = 1,
  thisMonth = 1,
}) => {
  const [interval, setInterval] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [notifyEnabled, setNotifyEnabled] = useState(false);
  const [email, setEmail] = useState('');
  const [selectedContact, setSelectedContact] = useState('');

  const hasNsWarning = nsChangeNeeded.length > 0;
  const showFollowUpForm = useNotifies || welcomeEmailEnabled;
  const showContactsSection = useAddressbook;
  const hasChanges = notifyEnabled || email.trim() !== '' || selectedContact !== '';

  return (
    <div className="hi-ui-add-hosting-confirmation">
      <Text variant="heading-1">Manage Hosting Accounts</Text>

      {sent && (
        <Notice variant="success" className="hi-ui-add-hosting-confirmation__banner">
          Your web hosting welcome email has been sent to the email address provided.
        </Notice>
      )}

      {errors.length > 0 && (
        <Notice variant="error" className="hi-ui-add-hosting-confirmation__banner">
          {errors.map((code, index) => (
            <div key={index}>{errorMessageFor(code)}</div>
          ))}
        </Notice>
      )}

      {addedDomain ? (
        <>
          <Notice variant="success" className="hi-ui-add-hosting-confirmation__banner">
            <div>
              Web Hosting account activated for <strong>{addedDomain}</strong>. To manage your web
              hosting accounts click <Link href={MANAGE_PACKAGES_URL}>here</Link>.
            </div>
            {hasNsWarning && (
              <div className="hi-ui-add-hosting-confirmation__ns-warning">
                You should change the nameservers on <strong>{nsChangeNeeded.join(', ')}</strong> to{' '}
                <strong>{bestNameservers.join(', ')}</strong> once you have set up the site.
              </div>
            )}
          </Notice>

          {showFollowUpForm && (
            <Card variant="surface" padding="md" className="hi-ui-add-hosting-confirmation__card">
              <Form action={CLEANUP_ACTION} method="POST">
                <input type="hidden" name="send_account_id" value={addedId} />
                <input type="hidden" name="add_type" value={addType} />
                <input type="hidden" name="setup_d" value={String(thisDay)} />
                <input type="hidden" name="setup_m" value={String(thisMonth)} />

                {useNotifies && (
                  <section className="hi-ui-add-hosting-confirmation__section">
                    <Text variant="heading-2">Set Renewal Notifications</Text>
                    <Text variant="body">
                      Get a reminder when this account is due for renewal (you can update this later
                      from the{' '}
                      <Link
                        href={`/manage/manage-package-details.cgi?id=${encodeURIComponent(addedId)}`}
                      >
                        package details
                      </Link>{' '}
                      page).
                    </Text>
                    <div className="hi-ui-add-hosting-confirmation__notify-controls">
                      <Checkbox
                        name="use_notify"
                        value="1"
                        label="Enable renewal notifications"
                        checked={notifyEnabled}
                        onChange={(e) => setNotifyEnabled(e.target.checked)}
                      />
                      <FormField label="Notification interval" labelPosition="left" fullWidth>
                        <Select
                          name="notify_interval"
                          value={interval}
                          onChange={(e) =>
                            setInterval(e.target.value as 'monthly' | 'quarterly' | 'yearly')
                          }
                          fullWidth
                        >
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="yearly">Yearly</option>
                        </Select>
                      </FormField>
                    </div>
                  </section>
                )}

                {welcomeEmailEnabled && (
                  <section className="hi-ui-add-hosting-confirmation__section">
                    <Text variant="heading-2">Send Welcome Email</Text>
                    <Text variant="body">
                      Enter the customer&apos;s email address to send them a welcome email (you can
                      resend this later from the{' '}
                      <Link href="/manage/manage-reseller-we.cgi">welcome emails</Link> page).
                    </Text>
                    <FormField label="Customer email" labelPosition="left" fullWidth>
                      <Input
                        type="email"
                        name="send_email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        fullWidth
                      />
                    </FormField>
                  </section>
                )}

                {showContactsSection && (
                  <section className="hi-ui-add-hosting-confirmation__section">
                    <Text variant="heading-2">Assign Customer Database Contact</Text>
                    <Text variant="body">
                      To assign a customer database contact, select one below or{' '}
                      <Link
                        href="/manage/manage-addresses.cgi?new=1"
                        target="_blank"
                        rel="noopener"
                      >
                        add a new contact
                      </Link>{' '}
                      (you can update this later from the{' '}
                      <Link
                        href={`/manage/manage-package-details.cgi?id=${encodeURIComponent(addedId)}`}
                      >
                        package details
                      </Link>{' '}
                      page).
                    </Text>
                    <FormField label="Contact" labelPosition="left" fullWidth>
                      <Select
                        name="contact"
                        value={selectedContact}
                        onChange={(e) => setSelectedContact(e.target.value)}
                        fullWidth
                      >
                        <option value="">None</option>
                        {contacts.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name} &lt;{c.email}&gt;
                          </option>
                        ))}
                      </Select>
                    </FormField>
                  </section>
                )}

                <div className="hi-ui-add-hosting-confirmation__submit">
                  <Button type="submit" variant="primary" disabled={!hasChanges}>
                    Save Settings
                  </Button>
                </div>
              </Form>
            </Card>
          )}

          {showFollowUpForm && addedId && (
            <Text variant="body" inline className="hi-ui-add-hosting-confirmation__alt-link">
              Or{' '}
              <Link href={`/manage/manage-package-details.cgi?id=${encodeURIComponent(addedId)}`}>
                click here to manage this account
              </Link>
              .
            </Text>
          )}
        </>
      ) : (
        <Text variant="body" inline className="hi-ui-add-hosting-confirmation__fallback">
          Please continue to <Link href={MANAGE_PACKAGES_URL}>manage your accounts</Link>.
        </Text>
      )}
    </div>
  );
};
